import React from 'react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { can } from '@/domain/roles';
import { fmtNumber, fmtPct, fmtPp, fmtSigned } from '@/domain/numbers';
import { daysBetweenInclusive, formatRangeShort } from '@/domain/time';
import { FIELD_LABELS, UNAVAILABLE_SHORT } from '@/lib/labels';
import { PageSkeleton, Panel, PanelHeader } from '@/components/app/States';
import { CoverageBadge, Note } from '@/components/app/Badges';
import { PageHeader, DefList } from '@/components/app/PageHeader';
import { DeltaBadge } from '@/components/app/Kpi';
import { cn } from '@/lib/utils';

function cell(m) {
  if (!m) return '–';
  if (m.status === 'unavailable' || m.value === null) return <span className="font-sans text-[var(--hr-amber)]">{UNAVAILABLE_SHORT[m.reason] ?? 'Utilgjengelig'}</span>;
  return (
    <>
      <span className="font-semibold text-[var(--hr-heading)]">{fmtNumber(m.value, m.decimals)}</span> <span className="text-[13px] text-[var(--hr-muted)]">{m.unit}</span>
      {m.status === 'partial' && <span className="ml-1 font-sans text-[13px] text-[var(--hr-amber)]">delvis</span>}
    </>
  );
}

function PeriodCell({ title, result, testId, accent }) {
  const shift = result.productionHours.value + result.stopHours.value;
  return (
    <div className={cn('min-w-0 px-5 py-4', accent && 'lg:border-l lg:border-[var(--hr-hairline)]')} data-testid={testId}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[16px] font-semibold text-[var(--hr-heading)]">{title}</h3>
        <span className="hr-tabular text-sm text-[var(--hr-muted)]">{daysBetweenInclusive(result.query.fromDate, result.query.toDate)} kalenderdager</span>
      </div>
      <div className="hr-num mt-1 text-[20px] font-semibold tracking-tight text-[var(--hr-heading)]">{formatRangeShort(result.query.fromDate, result.query.toDate)}</div>
      <DefList
        className="mt-4"
        cols={2}
        items={[
          { label: 'Registrerte produksjonsdager', value: result.productionDays },
          { label: 'Planlagt driftsfrie dager', value: result.plannedOffDays },
          { label: 'Skifttimer (produksjon + stopp)', value: `${fmtNumber(shift, 1)} h` },
          { label: 'Registrert produksjonstid', value: `${fmtNumber(result.productionHours.value, 1)} h` },
          { label: 'UTC-intervall', value: `[${result.fromUtc}, ${result.toUtc})`, className: 'text-[13px] font-normal text-[var(--hr-muted)]' },
        ]}
      />
      <div className="mt-4 flex flex-wrap gap-1.5">
        {result.coverage.map((c) => (
          <CoverageBadge key={c.field} status={c.status} label={`${FIELD_LABELS[c.field] ?? c.field} ${c.received}/${c.expected}`} />
        ))}
      </div>
    </div>
  );
}

export default function SammenlignPage() {
  const r = useResults();
  const { role } = useSession();
  if (!r.ready) return <PageSkeleton />;
  const { current: c, reference: ref, comparison: cmp } = r;
  const rows = cmp.rows.filter((row) => row.key !== 'costPerTonne' || can(role, 'view_costs'));

  return (
    <div className="space-y-6">
      <PageHeader title="Sammenlign" lead={`${formatRangeShort(c.query.fromDate, c.query.toDate)} mot ${formatRangeShort(ref.query.fromDate, ref.query.toDate)} · andeler i prosentpoeng, øvrige i prosent`}>
        <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => r.set({ sml: r.compareMode === 'manuell' ? null : `${r.compare.fromDate}_${r.compare.toDate}` })} data-testid="toggle-manual-comparison-button">
          {r.compareMode === 'manuell' ? 'Bruk forrige periode' : 'Velg referanse manuelt'}
        </Button>
      </PageHeader>
      {r.compareMode === 'manuell' && <Note>Manuell referanse er aktiv. Juster datoene under «Referanse» i filterraden.</Note>}

      <Panel className="grid lg:grid-cols-2" data-testid="compare-periods-panel">
        <PeriodCell title="Valgt periode" result={c} testId="compare-current-card" />
        <PeriodCell title="Referanseperiode" result={ref} testId="compare-reference-card" accent />
      </Panel>

      <Panel data-testid="comparison-panel">
        <PanelHeader title="Endring per måltall" description="Summert teller delt på summert nevner i hver periode – ikke snitt av dagstall." />
        <div className="hr-scroll overflow-x-auto">
          <Table data-testid="comparison-table" className="[&_td]:py-3 [&_th]:h-10">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Måltall</TableHead>
                <TableHead className="text-right">{formatRangeShort(c.query.fromDate, c.query.toDate)}</TableHead>
                <TableHead className="text-right">{formatRangeShort(ref.query.fromDate, ref.query.toDate)}</TableHead>
                <TableHead className="hidden text-right md:table-cell">Absolutt</TableHead>
                <TableHead className="text-right">Prosent</TableHead>
                <TableHead className="hidden text-right sm:table-cell">Prosentpoeng</TableHead>
                <TableHead className="hidden pr-5 lg:table-cell">Vurdering</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key} data-testid={`comparison-row-${row.key}`}>
                  <TableCell className="pl-5 font-medium">
                    {row.label}
                    {row.isIllustrative && <span className="ml-1.5 rounded-full bg-[var(--hr-amber-bg)] px-1.5 py-px text-[12px] font-medium text-[var(--hr-amber)]">illustrativ</span>}
                  </TableCell>
                  <TableCell className="hr-num text-right" data-testid={`comparison-current-${row.key}`}>{cell(row.current)}</TableCell>
                  <TableCell className="hr-num text-right" data-testid={`comparison-reference-${row.key}`}>{cell(row.reference)}</TableCell>
                  <TableCell className="hr-num hidden text-right md:table-cell">{row.absChange === null ? '–' : row.isShare ? fmtPp(row.absChange, row.decimals) : `${fmtSigned(row.absChange, row.decimals)} ${row.unit}`}</TableCell>
                  <TableCell className={cn('hr-num text-right', row.status === 'cannot' && 'font-sans text-[var(--hr-muted)]')} data-testid={`comparison-pct-${row.key}`}>
                    {row.isShare ? <span className="text-[var(--hr-muted)]" title="Andeler sammenlignes i prosentpoeng">–</span> : row.status === 'ok' && row.pctChange !== null ? fmtPct(row.pctChange, 2, true) : row.status === 'cannot' ? 'kan ikke beregnes' : '–'}
                  </TableCell>
                  <TableCell className="hr-num hidden text-right sm:table-cell" data-testid={`comparison-pp-${row.key}`}>{row.isShare && row.ppChange !== null ? fmtPp(row.ppChange, 2) : '–'}</TableCell>
                  <TableCell className="hidden pr-5 text-[13px] text-[var(--hr-muted)] lg:table-cell">
                    {row.status === 'unavailable' ? row.message : <DeltaBadge row={row} invertGood={['tonnes', 'productionHours', 'productionRate'].includes(row.key)} compact />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="space-y-1 border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] leading-5 text-[var(--hr-muted)]">
          {cmp.caveats.map((t) => (
            <p key={t}>{t}</p>
          ))}
        </div>
      </Panel>
    </div>
  );
}
